import React, { useCallback, useEffect } from "react";

import { isEmpty, isNull } from "lodash/fp";
import { useNavigate, useSearchParams } from "react-router-dom";

import TablePaginator from "@app/analytics/components/TablePaginator";
import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import useTopicReports from "@app/analytics/hooks/topics/useTopicReports";
import useCurrentLocationRef from "@app/analytics/hooks/useCurrentLocationRef";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import {
  OnOpenReportHandler,
  Report,
} from "@app/analytics/MeetingReports/types";
import useDateRangeStore from "@app/analytics/stores/useDateRangeStore";
import { getDateRanges } from "@app/analytics/utils";
import useMeetings from "@app/common/hooks/meetings/useMeetings";
import usePagination from "@app/common/hooks/usePagination";
import ROUTES from "@app/common/routes";
import Config from "@app/config";
import { usePageTitle } from "@app/utils/hooks";

import Stack from "@rui/Stack";

import useRecurringMeetings from "./hooks/useRecurringMeetings";
import useUnseenReports from "./hooks/useUnseenReports";
import MeetingTipsBanner from "./MeetingTipsBanner";
import ReportsMenu from "./ReportsMenu";
import ReportsTable from "./ReportsTable";
import { filterReports, groupRecurringMeetings } from "./utils";
import { PAGE_QUERY_PARAM_NAME } from "../MeetingReport/constants";

const SharedWithMe = () => {
  usePageTitle("Shared With Me");

  const [queryParams, setQueryParams] = useSearchParams();
  const navigate = useNavigate();
  const { teamId, userId } = useRollupContext();

  const { groupRecurring, reportViews, selectedTags, sourceFilters } =
    useReportsTable((state) => ({
      groupRecurring: state.groupRecurring,
      reportViews: state.reportViews,
      selectedTags: state.tags.selectedTags,
      sourceFilters: state.sources.selected,
    }));

  const dateRanges = useDateRangeStore(getDateRanges);

  const { meetings: allMeetings, isLoading: allMeetingsLoading } = useMeetings(
    {
      dateRanges,
      userId,
      teamId,
      includeSampleMeetings: true,
      view: "shared_with_me",
    },
    { refetchOnMount: true }
  );

  const { topics: allTopics, isLoading: allTopicsLoading } = useTopicReports({
    dateRange: dateRanges[0],
    view: "shared_with_me",
  });

  const { meetings: recurringMeetings, isLoading: recurringMeetingsLoading } =
    useRecurringMeetings(
      { dateRanges, userId, teamId },
      { enabled: groupRecurring }
    );

  const reportsLoading =
    allMeetingsLoading ||
    (Config.flags.waitForTopicReportsLoad && allTopicsLoading);
  const loading = reportsLoading || recurringMeetingsLoading;

  const reports = filterReports({
    meetings: groupRecurring
      ? groupRecurringMeetings(allMeetings, recurringMeetings)
      : allMeetings,
    sources: sourceFilters,
    topics: allTopics,
    tags: selectedTags,
    reportViews,
  });

  // when the user visits the page, reset the last seen date and count (managed in hook)
  const { resetLastSeenSharedWithMe, lastSeenSharedWithMeDate } =
    useUnseenReports({ dateRanges });

  useEffect(() => {
    if (reportsLoading || isEmpty(reports)) return;

    // this timeout delay is required to avoid an error because otherwise,
    // two components are updating at the same time which causes React to error
    const timeoutDelay = isNull(lastSeenSharedWithMeDate) ? 250 : 3000;
    const timeout = setTimeout(() => {
      resetLastSeenSharedWithMe();
    }, timeoutDelay);

    return () => clearTimeout(timeout);
  }, [reportsLoading]);

  const pagination = usePagination<Report>(reports, {
    loading,
    resetScrollOnPageChange: true,
    queryParamOptions: {
      setQueryParams,
      queryParams,
      queryParamName: PAGE_QUERY_PARAM_NAME,
    },
  });

  const page = Number(queryParams.get(PAGE_QUERY_PARAM_NAME));
  useEffect(() => {
    if (!loading && page <= pagination.numPages) {
      pagination.goToPage(page);
    }
  }, [page, pagination.numPages, loading]);

  const currentLocationRef = useCurrentLocationRef();

  const handleOpenReport: OnOpenReportHandler = useCallback((id, opts) => {
    const route =
      opts.type === "topic"
        ? ROUTES.topicReportById(id, opts.ownerUserId)
        : ROUTES.meetingReportById(id);
    if (opts.newTab) {
      window.open(route, "_blank");
    } else {
      navigate(route, {
        state: {
          from: currentLocationRef.current,
        },
      });
    }
  }, []);

  return (
    <>
      <ReportsMenu
        isLoading={loading}
        pagination={pagination}
        view="shared_with_me"
      />
      <MeetingTipsBanner />
      <Stack sx={{ width: "100%", overflowX: "auto" }}>
        <ReportsTable
          isLoading={loading}
          onOpenReport={handleOpenReport}
          pagination={pagination}
          reports={reports}
          lastSeenDate={lastSeenSharedWithMeDate}
        />
      </Stack>
      <Stack variant="right" sx={{ width: "100%", mt: 2 }}>
        <TablePaginator
          {...pagination}
          loading={loading}
          showPageSizeSelector
        />
      </Stack>
    </>
  );
};

export default SharedWithMe;
